import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad } from "@redotech/react-util/load";
import { RedoMarketplaceRpcClientContext } from "@redotech/redo-marketplace-app-common/rpc-client";
import { VendorAuthContext } from "@redotech/redo-marketplace-app-common/vendor-auth";
import { MarketplaceCartItem } from "@redotech/redo-model/recommerce/marketplace-cart";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoSpinner } from "@redotech/redo-web/arbiter-components/loading/redo-spinner";
import { Breadcrumbs } from "@redotech/redo-web/breadcrumb";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useContext, useState } from "react";
import { useParams } from "react-router-dom";
import { BulkPurchaseTable } from "../components";

export const BrandBulkPurchase = memo(function BrandBulkPurchase() {
  const { brandId } = useParams<{ brandId: string }>();
  const vendorAuth = useContext(VendorAuthContext);
  const marketplaceRpcClient = useRequiredContext(
    RedoMarketplaceRpcClientContext,
  );

  const vendorName = brandId?.trim();

  const productsResult = useLoad(async () => {
    if (!vendorAuth || !vendorName) {
      return { products: [], initialCartItems: [] };
    }

    const result = await marketplaceRpcClient.getMarketplaceProductsByVendor({
      marketplaceType: "REDO",
      vendor: vendorName,
      pagination: { limit: 500, offset: 0 },
    });

    // Create initial cart items with full available quantities
    const initialCartItems: MarketplaceCartItem[] = [];
    for (const product of result.products) {
      for (const variant of product.variants) {
        if (variant.available > 0) {
          initialCartItems.push({
            productId: product._id,
            variantId: variant._id,
            quantity: variant.available,
          });
        }
      }
    }

    setSelectedCartItems(initialCartItems);
    return { products: result.products };
  }, [marketplaceRpcClient, vendorAuth, vendorName]);

  const [isPurchasing, setIsPurchasing] = useState(false);
  const [selectedCartItems, setSelectedCartItems] = useState<
    MarketplaceCartItem[]
  >([]);

  const handleBulkPurchase = useHandler(async () => {
    if (!vendorName || selectedCartItems.length === 0) return;

    setIsPurchasing(true);
    try {
      const result = await marketplaceRpcClient.doBulkPurchase({
        cartItems: selectedCartItems,
        currency: "USD",
      });

      // Redirect to Stripe checkout
      window.location.href = result.checkoutUrl;
    } catch (error) {
      console.error("Error creating bulk purchase:", error);
    } finally {
      setIsPurchasing(false);
    }
  });

  if (!vendorName) {
    return (
      <Flex align="center" justify="center" p="xl">
        <Text>Brand not found</Text>
      </Flex>
    );
  }

  return (
    <div className="flex flex-1 flex-col">
      <div className="px-6 py-4 ">
        <Breadcrumbs size="small" />
        <div className="flex items-center justify-between mt-2">
          <Text fontSize="2xl" fontWeight="bold">
            {vendorName}
          </Text>
          <Text color="secondary" fontSize="lg">
            {productsResult.value?.products.length || 0} products
          </Text>
        </div>
      </div>

      {/* Main content - Scrollable */}
      <div className=" h-full overflow-auto p-6 flex-grow-0">
        {productsResult.error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
            <Text color="red">
              Error loading products: {productsResult.error.message}
            </Text>
          </div>
        )}

        {productsResult.value?.products.length === 0 && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
            <Text color="secondary">No products found for this brand.</Text>
          </div>
        )}

        {productsResult.pending && (
          <div className="flex justify-center py-12">
            <RedoSpinner size="lg" />
          </div>
        )}

        {productsResult.value && productsResult.value.products.length > 0 && (
          <BulkPurchaseTable
            onQuantitiesChange={setSelectedCartItems}
            products={productsResult.value.products}
          />
        )}
      </div>

      {/* Footer - Fixed at bottom */}
      {productsResult.value && productsResult.value.products.length > 0 && (
        <div className="bg-white border-t border-gray-200 px-6 py-4 sticky bottom-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Text color="secondary" fontSize="sm">
                Total quantity:
              </Text>
              <Text fontSize="sm" fontWeight="semibold">
                {selectedCartItems.reduce(
                  (sum, item) => sum + item.quantity,
                  0,
                )}
              </Text>
            </div>

            <RedoButton
              disabled={isPurchasing || selectedCartItems.length === 0}
              hierarchy="primary"
              onClick={handleBulkPurchase}
              size="lg"
              text={isPurchasing ? "Processing..." : "Purchase"}
            />
          </div>
        </div>
      )}
    </div>
  );
});
